<span *ngIf="userGroupFormGroup.dirty">You have unsaved changes</span>
<form *ngIf="canEditUserGroup || !isEditForm; else viewOnly" class="team-form">
  <span class="pull-right color-red font-size-3xs"> * Required </span>
  <!--
    v2.4 top section: 2-column grid that auto-flows fields row-by-row in
    DOM order. The order below — Team Name, Region or Station, Forest or
    Lab, Point of Contact — gives a desktop 2x2 of
      row 1: Team Name        | Region or Station
      row 2: Forest or Lab    | Point of Contact (combobox + error + chip)
    AND, when the grid collapses to a single column on mobile, the same DOM
    order produces the stack the spec calls for (Team Name → Region/Station
    → Forest/Lab → POC). The POC's auxiliary blocks (error, desktop email
    lines, mobile card) sit inside a wrapper cell so they ride with the
    combobox across viewports.
  -->
  <div class="team-form__top-grid">
    <ciao-form-field
      type="text"
      label="Team Name"
      [formControlInput]="userGroupControls.label"
    ></ciao-form-field>
    <ng-container *ngIf="canEditParentGroup; else parentGroupReadOnly">
      <ciao-form-field
        type="dropdown"
        label="Region or Station"
        [formControlInput]="userGroupControls.regionStation"
        [selectOptions$]="allRegionsOptions$"
      ></ciao-form-field>
      <ciao-form-field
        type="dropdown"
        label="Forest or Lab"
        [formControlInput]="userGroupControls.forestLab"
        [selectOptions$]="allForestLabOptions$"
      ></ciao-form-field>
    </ng-container>
    <ng-template #parentGroupReadOnly>
      <dl class="view-only-field">
        <dt>Region or Station</dt>
        <dd>{{ regionStation?.labelPrefix }}{{ regionStation?.label }}</dd>
      </dl>
      <dl class="view-only-field">
        <dt>Forest or Lab</dt>
        <dd>{{ forestLab?.labelPrefix }}{{ forestLab?.label }}</dd>
      </dl>
    </ng-template>
    <div class="team-form__poc-cell">
      <!--
        v2.4 POC: usda-only searchable combobox. The form's pointOfContact and
        contactEmail are kept (hidden) and mirrored from this control so the
        save path is unchanged. The orphaned-contact error renders below the
        combobox when the saved contactEmail no longer matches a USDA user.
      -->
      <ciao-form-field
        label="Point of Contact"
        placeholder="Add a Team Point of Contact (usda.gov only)"
        type="combobox"
        subtype="people-usda"
        [formControlInput]="pointOfContactPersonControl"
        [compareWith]="comparePocById"
        [requiredProxy]="true"
      ></ciao-form-field>
      <div class="poc-error" *ngIf="pocOrphaned" role="alert">
        This contact is no longer found in the USDA user directory. A
        supervisor or admin must update the point of contact.
      </div>
      <!--
        Desktop POC details: plain email + phone below the dropdown, no card
        chrome, no labels, no Remove (operator changes POC by re-selecting
        in the combobox). Phone is a placeholder until full Person details
        carry cellPhone through to PersonAttributesSlim.

        Hidden on mobile via media query; mobile renders the POC as a
        person-card row below, matching the other sections' visual pattern.
      -->
      <div
        class="poc-details"
        *ngIf="pointOfContactPersonControl.value as poc"
      >
        <div class="poc-details__line">
          {{ extractEmailFromDisplayName(poc.displayNameDropdown) || "—" }}
        </div>
        <div
          class="poc-details__line"
          [class.poc-details__line--muted]="!poc.cellPhone"
        >
          {{ poc.cellPhone || "No phone on file" }}
        </div>
      </div>
      <!--
        Mobile-only POC person-card. Reuses the same row component as the
        other sections so the accordion / avatar / Remove styling all match.
        Remove resets the POC control; the dropdown above stays available
        for re-selecting.
      -->
      <ciao-team-member-row
        class="poc-mobile-card"
        *ngIf="pointOfContactPersonControl.value as poc"
        [person]="poc"
        [canRemove]="canEditUserGroup"
        (remove)="pointOfContactPersonControl.reset()"
      ></ciao-team-member-row>
    </div>
  </div>
  <!--
    Contact Email is being retired in v2.4 — replaced by the POC dropdown in
    Phase 2. Field is kept on the form group (no data semantics change yet) but
    hidden so the layout matches the new spec.
  -->
  <ciao-form-field
    *ngIf="false"
    type="text"
    label="Contact Email"
    [formControlInput]="userGroupControls.contactEmail"
  ></ciao-form-field>
  <ciao-form-field
    *ngIf="false"
    type="text"
    label="Description"
    [formControlInput]="userGroupControls.description"
  ></ciao-form-field>
</form>
<ng-template #viewOnly>
  <!--
    View-only layout mirrors the edit form: 4 fields as direct grid children
    in DOM order (Team Name, Region/Station, Forest/Lab, POC). Desktop fills
    row-by-row into the 2-column grid; mobile collapses to a single column
    and gets the spec stack (Team Name → Region → Forest/Lab → POC) for free.
  -->
  <div class="team-form__top-grid">
    <dl class="view-only-field">
      <dt>Team Name</dt>
      <dd>{{ userGroupFormGroup.value.label }}</dd>
    </dl>
    <dl class="view-only-field">
      <dt>Region or Station</dt>
      <dd>{{ regionStation?.labelPrefix }}{{ regionStation?.label }}</dd>
    </dl>
    <dl class="view-only-field">
      <dt>Forest or Lab</dt>
      <dd>{{ forestLab?.labelPrefix }}{{ forestLab?.label }}</dd>
    </dl>
    <dl class="view-only-field">
      <dt>Point of Contact</dt>
      <dd>{{ userGroupFormGroup.value.pointOfContact || "—" }}</dd>
      <dd class="view-only-field__sub">
        {{ userGroupFormGroup.value.contactEmail || "—" }}
      </dd>
      <dd
        class="view-only-field__sub"
        [class.view-only-field__sub--muted]="!pointOfContactPersonControl.value?.cellPhone"
      >
        {{ pointOfContactPersonControl.value?.cellPhone || "No phone on file" }}
      </dd>
    </dl>
  </div>
</ng-template>

<!-- ============================== Supervisors / Admins ============================== -->
<div class="section-header">
  <span class="section-header__label">
    Supervisors/Admins<span class="required-star" *ngIf="canEditSupervisors" aria-label="Required">*</span>
  </span>
  <ciao-tool-tip
    class="section-header__info"
    title="Supervisors/Admins"
    message="USDA.gov user who oversees team roles"
  ></ciao-tool-tip>
  <span class="section-header__sublabel">Manage all team roles</span>
</div>
<!--
  inputId is set explicitly because the section header above now carries the
  section's title; the form-field's own label is intentionally omitted so the
  matLabel row doesn't render. FieldComponent.calculateId throws unless one of
  the two is provided.
-->
<ciao-form-field
  *ngIf="canEditSupervisors"
  inputId="usergroup-form-supervisor-combo"
  placeholder="Add Supervisor or Admin (usda.gov only)"
  type="combobox"
  subtype="people-usda"
  [formControlInput]="membersControls.nextSupervisor"
  [requiredProxy]="true"
  toolTip="Required: Select at least one (1) Supervisor. A member can hold only one role on a team, so a Supervisor cannot also be a Team Member, Dispatcher, or Guest."
  toolTipTitle="Description"
></ciao-form-field>
<!--
  No guest badge on supervisors: supervisor is USDA-only by role-restriction
  rules, so a guest can never hold it. team_member and dispatcher CAN be held
  by guests (auto-upgrade workflow / non-USDA dispatchers), so their lists
  render the badge below.
-->
<ciao-team-member-row
  *ngFor="let person of membersControls.listSupervisor.value"
  [person]="person"
  [canRemove]="canEditSupervisors"
  (remove)="removePersonFromList(person, membersControls.listSupervisor)"
></ciao-team-member-row>

<!-- ================================== Team Members ================================== -->
<ng-container *ngIf="canViewTeamMembers">
  <div class="section-header">
    <span class="section-header__label">
      Team Members<span class="required-star" *ngIf="canEditTeamMembers" aria-label="Required">*</span>
    </span>
    <ciao-tool-tip
      class="section-header__info"
      title="Team Members"
      message="USDA.gov user participating in team"
    ></ciao-tool-tip>
    <span class="section-header__sublabel">Full trip edit permissions</span>
  </div>
  <ciao-form-field
    *ngIf="canEditTeamMembers"
    inputId="usergroup-form-team-member-combo"
    placeholder="Add Team Member (usda.gov only)"
    type="combobox"
    subtype="people-usda"
    [formControlInput]="membersControls.nextTeamMember"
    [requiredProxy]="true"
    toolTip="Required: Select at least one (1) Team Member. A member can hold only one role on a team, so a Team Member cannot also be a Supervisor, Dispatcher, or Guest."
    toolTipTitle="Description"
  ></ciao-form-field>
  <ng-container *ngFor="let person of membersControls.listTeamMember.value">
    <ciao-team-member-row
      [person]="person"
      [canRemove]="canEditTeamMembers"
      (remove)="removePersonFromList(person, membersControls.listTeamMember)"
    ></ciao-team-member-row>
  </ng-container>

  <!-- =================================== Dispatchers =================================== -->
  <div class="section-header">
    <span class="section-header__label">Dispatchers</span>
    <ciao-tool-tip
      class="section-header__info"
      title="Dispatchers"
      message="Non-USDA user who manages trips"
    ></ciao-tool-tip>
    <span class="section-header__sublabel">Edit trips, add Dispatchers</span>
  </div>
  <ciao-form-field
    *ngIf="canEditDispatchers"
    inputId="usergroup-form-dispatcher-combo"
    placeholder="Add Dispatcher (non-usda.gov only)"
    type="combobox"
    subtype="people-non-usda"
    [formControlInput]="membersControls.nextDispatcher"
    toolTip="Non-USDA user who manages trips"
    toolTipTitle="Description"
  ></ciao-form-field>
  <ng-container *ngFor="let person of membersControls.listDispatcher.value">
    <ciao-team-member-row
      [person]="person"
      [canRemove]="canEditDispatchers"
      (remove)="removePersonFromList(person, membersControls.listDispatcher)"
    ></ciao-team-member-row>
  </ng-container>

  <!-- ====================================== Guests ====================================== -->
  <div class="section-header">
    <span class="section-header__label">Guests</span>
    <ciao-tool-tip
      class="section-header__info"
      title="Guests"
      message="Non-USDA user with limited access"
    ></ciao-tool-tip>
    <span class="section-header__sublabel">Edit assigned trips only</span>
  </div>
  <ciao-form-field
    *ngIf="canEditGuests"
    inputId="usergroup-form-guest-combo"
    placeholder="Add Guest (non-usda.gov only)"
    type="combobox"
    subtype="people-non-usda"
    [formControlInput]="membersControls.nextGuest"
    toolTip="Non-USDA user with limited access to assigned trips"
    toolTipTitle="Description"
  ></ciao-form-field>
  <ciao-team-member-row
    *ngFor="let person of membersControls.listGuest.value"
    [person]="person"
    [canRemove]="canEditGuests"
    (remove)="removePersonFromList(person, membersControls.listGuest)"
  ></ciao-team-member-row>
  <div
    class="guests-empty-state"
    *ngIf="membersControls.listGuest.value?.length === 0"
  >
    No guests added yet
  </div>
</ng-container>

<ciao-modal #confirmLeaveModal title="Unsaved Changes">
  <span modalBody>
    You have unsaved changes. Are you sure you want to abandon them?
  </span>
  <span modalFooter>
    <button class="btn" (click)="confirmLeaveModal.dismiss('cancel')">
      Cancel
    </button>
    <button class="btn-danger" (click)="confirmLeaveModal.close('confirm')">
      Confirm
    </button>
  </span>
</ciao-modal>
